import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Divider from '@material-ui/core/Divider';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import BlockIcon from '@material-ui/icons/Block';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import LinkIcon from '@material-ui/icons/Link';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import RefreshIcon from '@material-ui/icons/Refresh';
import SendIcon from '@material-ui/icons/Send';

import { hasExpiringTokens } from '../../lib/helpers/contact-access-token.helpers';
import { useHoverSubmenu } from '../../lib/hooks/use-hover-submenu';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { contactLinksOf, hasAnyReportCardAction, reportCardActionOptions } from './report-card-access.helpers';
import { IContactLink, ReportCardWrite } from './report-card-access.types';
import { ReportCardActionsButton } from './report-card-actions-button.component';
import { ReportCardContactLinksMenu } from './report-card-contact-links-menu.component';
import { useReportCardActionsContext } from './report-card-actions.context';
import { useReportCardActionsStyles } from './report-card-actions.styles';

interface IReportCardActionsProps {
  // Needs player.code, contacts with their tokens, ageGroup { _id assessmentId } and reportCardAccess.
  playerAssessment: IPlayerAssessment;
  // Opens the menu as it mounts, for a row that loaded it on demand after a click on its trigger.
  openOnMount?: boolean;
}

// One player's report card menu. The writes and the confirm dialog come from the provider above the
// table or page; the roles are this player's age group's, since a table can span several.
export const ReportCardActions: React.FC<IReportCardActionsProps> = ({ playerAssessment, openOnMount = false }) => {
  const classes = useReportCardActionsStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (openOnMount && buttonRef.current) setAnchorEl(buttonRef.current);
  }, [openOnMount]);
  const submenu = useHoverSubmenu();
  const {
    messages, surfaceFor, isInFlight, request, copyLink, view,
  } = useReportCardActionsContext();
  const { canManage, canOpen } = surfaceFor(playerAssessment.ageGroup?._id);
  const inFlight = isInFlight(playerAssessment._id);

  const contacts = playerAssessment.contacts;
  const contactLinks = useMemo(() => contactLinksOf(contacts || []), [contacts]);
  const options = useMemo(() => reportCardActionOptions(playerAssessment.reportCardAccess, {
    canManage,
    canOpen,
    hasCopyableLink: contactLinks.length > 0,
    hasExpiringLinks: hasExpiringTokens(contacts || [], playerAssessment._id),
  }), [playerAssessment.reportCardAccess, playerAssessment._id, canManage, canOpen, contactLinks, contacts]);

  const { close: closeSubmenu } = submenu;
  const closeMenu = useCallback(() => {
    closeSubmenu();
    setAnchorEl(null);
  }, [closeSubmenu]);

  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);

  const choose = useCallback((action: ReportCardWrite) => {
    closeMenu();
    request(action, playerAssessment);
  }, [closeMenu, request, playerAssessment]);

  const handleView = useCallback(() => {
    closeMenu();
    view(playerAssessment);
  }, [closeMenu, view, playerAssessment]);

  const handleCopy = useCallback((contactLink: IContactLink) => {
    closeMenu();
    copyLink(playerAssessment, contactLink);
  }, [closeMenu, copyLink, playerAssessment]);

  const hasWrites = options.send || options.resend || options.copyLink || options.extendLinks || options.revoke || options.reEnable;
  if (!hasAnyReportCardAction(options)) return null;

  const idSuffix = playerAssessment._id;

  return (
    <>
      <ReportCardActionsButton
        ref={buttonRef}
        playerAssessmentId={idSuffix}
        onClick={openMenu}
        busy={inFlight}
        menuOpen={Boolean(anchorEl)}
      />

      <Menu
        id={`report-card-menu-${idSuffix}`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        className={classes.menu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        elevation={8}
        getContentAnchorEl={null}
        MenuListProps={{ 'aria-labelledby': `report-card-actions-${idSuffix}`, role: 'menu' }}
      >
        <div className={classes.menuHeader}>
          <Typography variant="caption"><IntlMsg {...messages.lblActions} /></Typography>
        </div>

        {options.view && (
          <MenuItem onClick={handleView} className={classes.menuItem} role="menuitem">
            <ListItemIcon className={classes.menuItemIcon}><AssignmentTurnedInIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionView} />} />
          </MenuItem>
        )}

        {options.view && hasWrites && <Divider />}

        {options.send && (
          <MenuItem onClick={() => choose('SEND')} className={classes.menuItem} role="menuitem">
            <ListItemIcon className={classes.menuItemIcon}><SendIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionSend} />} secondary={<IntlMsg {...messages.captionThisPlayerOnly} />} />
          </MenuItem>
        )}

        {options.resend && (
          <MenuItem onClick={() => choose('RESEND')} className={classes.menuItem} role="menuitem">
            <ListItemIcon className={classes.menuItemIcon}><SendIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionResend} />} secondary={<IntlMsg {...messages.captionThisPlayerOnly} />} />
          </MenuItem>
        )}

        {options.copyLink && (
          <MenuItem
            id={`copy-contact-link-${idSuffix}`}
            {...submenu.triggerProps}
            className={classes.submenuTrigger}
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={Boolean(submenu.anchorEl)}
            aria-owns={submenu.anchorEl ? `contact-links-menu-${idSuffix}` : undefined}
          >
            <ListItemIcon className={classes.menuItemIcon}><LinkIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionCopyLink} />} secondary={<IntlMsg {...messages.captionCopyLink} />} />
            <KeyboardArrowRightIcon className={classes.submenuChevron} />
          </MenuItem>
        )}

        {options.extendLinks && (
          <MenuItem onClick={() => choose('EXTEND_LINKS')} className={classes.menuItem} role="menuitem">
            <ListItemIcon className={classes.menuItemIcon}><RefreshIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionExtendLinks} />} secondary={<IntlMsg {...messages.captionExtendLinks} />} />
          </MenuItem>
        )}

        {options.revoke && (
          <MenuItem onClick={() => choose('REVOKE')} className={classes.menuItem} role="menuitem">
            <ListItemIcon className={classes.menuItemIcon}><BlockIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionRevoke} />} secondary={<IntlMsg {...messages.captionRevoke} />} />
          </MenuItem>
        )}

        {options.reEnable && (
          <MenuItem onClick={() => choose('RE_ENABLE')} className={classes.menuItem} role="menuitem">
            <ListItemIcon className={classes.menuItemIcon}><LockOpenIcon /></ListItemIcon>
            <ListItemText primary={<IntlMsg {...messages.actionReEnable} />} secondary={<IntlMsg {...messages.captionReEnable} />} />
          </MenuItem>
        )}
      </Menu>

      <ReportCardContactLinksMenu
        id={`contact-links-menu-${idSuffix}`}
        triggerId={`copy-contact-link-${idSuffix}`}
        title={messages.lblSelectContact}
        anchorEl={submenu.anchorEl}
        contactLinks={contactLinks}
        menuListProps={submenu.menuListProps}
        onCopy={handleCopy}
        onClose={closeMenu}
      />
    </>
  );
};
